/**
 * useEncuestaPendiente — resuelve si el usuario autenticado tiene una encuesta
 * activa de su audiencia pendiente de responder.
 *
 * Lecturas directas vía RLS (definición tenant-read; respuestas solo la propia
 * fila). El submit va por RPC `encuesta_submit` (mig 0096).
 */
import { useCallback, useEffect, useState } from 'react';
import { getSupabase, useUser } from '@aosenuma/lib-supabase';
import type {
  Encuesta,
  EncuestaAudienciaRespondente,
  EncuestaPregunta,
  EncuestaSeccion,
} from '@aosenuma/lib-types';

export interface EncuestaPendienteState {
  loading: boolean;
  /** Encuesta activa para la audiencia, o null si no hay. */
  encuesta: Encuesta | null;
  secciones: EncuestaSeccion[];
  /** Preguntas ya filtradas a la audiencia del usuario, ordenadas. */
  preguntas: EncuestaPregunta[];
  /** true si el usuario ya envió su respuesta. */
  yaRespondida: boolean;
  /** Re-consulta el estado (p. ej. tras enviar). */
  refresh: () => void;
}

interface RawOpciones {
  opciones: unknown;
}

function parseOpciones(row: EncuestaPregunta & RawOpciones): EncuestaPregunta {
  const raw = row.opciones;
  const opciones = Array.isArray(raw) ? raw.filter((o): o is string => typeof o === 'string') : [];
  return { ...row, opciones };
}

export function useEncuestaPendiente(
  audiencia: EncuestaAudienciaRespondente,
): EncuestaPendienteState {
  const { user } = useUser();
  const [loading, setLoading] = useState(true);
  const [encuesta, setEncuesta] = useState<Encuesta | null>(null);
  const [secciones, setSecciones] = useState<EncuestaSeccion[]>([]);
  const [preguntas, setPreguntas] = useState<EncuestaPregunta[]>([]);
  const [yaRespondida, setYaRespondida] = useState(false);
  const [nonce, setNonce] = useState(0);

  const refresh = useCallback(() => setNonce((n) => n + 1), []);

  useEffect(() => {
    if (!user?.id) {
      setLoading(!user ? true : false);
      return;
    }
    let cancelled = false;
    const sb = getSupabase();

    async function load(userId: string): Promise<void> {
      try {
        const { data: encRows, error: encErr } = await sb
          .from('encuestas')
          .select('id, tenant_id, slug, titulo, descripcion, audiencia, estado, abre_at, cierra_at, created_at')
          .eq('estado', 'activa')
          .in('audiencia', ['ambas', audiencia])
          .order('created_at', { ascending: false })
          .limit(1);
        if (encErr) throw encErr;
        const enc = (encRows?.[0] as Encuesta | undefined) ?? null;
        if (cancelled) return;
        if (!enc) {
          setEncuesta(null);
          setLoading(false);
          return;
        }

        const [secRes, pregRes, respRes] = await Promise.all([
          sb
            .from('encuesta_secciones')
            .select('id, encuesta_id, orden, titulo, descripcion')
            .eq('encuesta_id', enc.id)
            .order('orden', { ascending: true }),
          sb
            .from('encuesta_preguntas')
            .select('id, encuesta_id, seccion_id, orden, tipo, texto, ayuda, opciones, requerida, audiencia')
            .eq('encuesta_id', enc.id)
            .in('audiencia', ['ambas', audiencia])
            .order('orden', { ascending: true }),
          sb
            .from('encuesta_respuestas')
            .select('id')
            .eq('encuesta_id', enc.id)
            .eq('user_id', userId)
            .maybeSingle(),
        ]);
        if (cancelled) return;
        if (secRes.error) throw secRes.error;
        if (pregRes.error) throw pregRes.error;
        // respRes.error se tolera (RLS puede regresar 0 filas sin error)

        setEncuesta(enc);
        setSecciones((secRes.data ?? []) as EncuestaSeccion[]);
        setPreguntas(((pregRes.data ?? []) as (EncuestaPregunta & RawOpciones)[]).map(parseOpciones));
        setYaRespondida(Boolean(respRes.data));
        setLoading(false);
      } catch (err) {
        if (!cancelled) {
          // Falla de red / RLS: no bloquear la app; la encuesta simplemente no aparece.
          console.warn('[encuestas] no se pudo cargar la encuesta pendiente:', err);
          setEncuesta(null);
          setLoading(false);
        }
      }
    }

    setLoading(true);
    void load(user.id);
    return () => {
      cancelled = true;
    };
  }, [user, audiencia, nonce]);

  return { loading, encuesta, secciones, preguntas, yaRespondida, refresh };
}
